import React from "react";
import AssetCard from "../AssetCard";

export interface Asset {
  id: number;
  model: string;
  brand: string;
  config: {
    id: number;
    ram: number;
    processor: string;
    harddisk: number;
    harddiskType: string;
    operatingSystem: string;
  };
  ownedDevice: {
    id: number;
    purchaseDate: string | null;
    cost: number;
    s3InvoiceUrl: string | null;
    depreciationRate: number;
    usefulLife: number;
  } | null;
  rentedDevice: unknown;
  sampleDevice: unknown;
  purchaseDate: string;
  validityFrom: string;
  validityTo: string;
  assign: string;
  assignDate: string;
  releaseDate: string;
  type: string;
  status: string;
  owner: string;
  location: string;
  identityType: string;
  identity: string;
  createdBy: string;
  created: string;
  modifiedBy: string;
  modified: string;
  assetPassword: string;
  userEmail: string;
  urls: unknown[];
  asset_type: string;
}

export interface AssetsListProps {
  assets: Asset[];
  gridCols?: {
    default?: number;
    sm?: number;
    md?: number;
    lg?: number;
    xl?: number;
  };
  onAssetView?: (id: number) => void;
  emptyStateConfig?: {
    message?: string;
    actionLabel?: string;
    onAction?: () => void;
    showAction?: boolean;
  };
}

const AssetsList: React.FC<AssetsListProps> = ({
  assets,
  gridCols = {
    default: 1,
    md: 2,
    lg: 3,
    xl: 4,
  },
  onAssetView,
  emptyStateConfig = {
    message: "No assets assigned",
    actionLabel: "Assign Asset",
    showAction: false,
  },
}) => {
  if (!assets || assets.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center text-center  py-12 px-6 bg-gray-50 dark:bg-gray-800 rounded-lg">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-16 w-16 text-gray-300 dark:text-gray-600 mb-3"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1}
            d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"
          />
        </svg>
        <p className="text-gray-500 dark:text-gray-400 text-base mb-4">
          {emptyStateConfig.message}
        </p>
        {emptyStateConfig.showAction && emptyStateConfig.onAction && (
          <button
            onClick={emptyStateConfig.onAction}
            className="px-5 py-2.5 bg-blue-600 text-white hover:bg-blue-700 rounded-md text-sm font-medium transition-colors duration-200 shadow-sm"
          >
            {emptyStateConfig.actionLabel}
          </button>
        )}
      </div>
    );
  }

  const getGridClasses = () => {
    const classes: string[] = ["grid", "gap-4"];

    if (gridCols.default) classes.push(`grid-cols-${gridCols.default}`);
    if (gridCols.sm) classes.push(`sm:grid-cols-${gridCols.sm}`);
    if (gridCols.md) classes.push(`md:grid-cols-${gridCols.md}`);
    if (gridCols.lg) classes.push(`lg:grid-cols-${gridCols.lg}`);
    if (gridCols.xl) classes.push(`xl:grid-cols-${gridCols.xl}`);

    return classes.join(" ");
  };

  return (
    <div className={getGridClasses()}>
      {assets.map((asset) => (
        <AssetCard
          key={asset.id}
          id={asset.id}
          model={asset.model}
          brand={asset.brand}
          type={asset.type}
          status={asset.status}
          owner={asset.owner}
          location={asset.location}
          identityType={asset.identityType}
          identity={asset.identity}
          assign={asset.assign}
          assignDate={asset.assignDate}
          purchaseDate={asset.purchaseDate}
          assetType={asset.asset_type}
          config={asset.config}
          onView={onAssetView}
        />
      ))}
    </div>
  );
};

export default AssetsList;
